import React, { useMemo, useState } from 'react';
import { ExclamationCircleIcon } from '@heroicons/react/24/outline';
import { Alert, AlertTitle, AlertDescription } from '@dashboard/components/ui/alert';
import { DataGrid } from '@dashboard/components/DataGrid';
import EmptyState from '@dashboard/components/EmptyState';
import { Button } from '@dashboard/components/ui/button';
import ApiKeyDeleteDialog from './ApiKeyDeleteDialog';
import StatusBadge from '@/dashboard/components/StatusBadge';
import { formatDateTime } from '@/dashboard/lib/format-date';
import Dialog from '@dashboard/components/Dialog';
import EventsLog from '@dashboard/components/EventsLog/EventsLog';

const formatLastUsed = lastUsedAt => {
  if (!lastUsedAt) return 'Never';
  return formatDateTime(lastUsedAt);
};

const getStatusAppearance = isDeleted => (isDeleted ? 'error' : 'success');

const ApiKeyTable = ({ data, isLoading, isError }) => {
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedApiKey, setSelectedApiKey] = useState(null);

  const handleDeleteClick = apiKey => {
    setSelectedApiKey(apiKey);
    setDeleteDialogOpen(true);
  };

  const columns = useMemo(
    () => [
      {
        accessorKey: 'name',
        header: 'Name',
        cell: ({ row }) => row.original.name,
      },
      {
        accessorKey: 'keyPreview',
        header: 'Key preview',
        cell: ({ row }) => (
          <code className="text-sm bg-gray-100 dark:bg-muted px-2 py-1 rounded">
            {row.original.keyPreview}
          </code>
        ),
      },
      {
        accessorKey: 'lastUsedAt',
        header: 'Last used',
        cell: ({ row }) => formatLastUsed(row.original.lastUsedAt),
      },
      {
        accessorKey: 'usageCount',
        header: 'Usage',
        cell: ({ row }) => row.original.usageCount || 0,
        meta: { className: 'text-center' },
      },
      {
        accessorKey: 'isDeleted',
        header: 'Status',
        cell: ({ row }) => (
          <StatusBadge
            appearance={getStatusAppearance(row.original.isDeleted)}
            label={row.original.isDeleted ? 'Inactive' : 'Active'}
          />
        ),
      },
      {
        accessorKey: 'createdAt',
        header: 'Created',
        cell: ({ row }) => formatDateTime(row.original.createdAt),
      },
      {
        id: 'actions',
        header: 'Actions',
        cell: ({ row }) => {
          const apiKey = row.original;
          return (
            <div className="flex items-center gap-2">
              {apiKey.integrationId && (
                <Dialog
                  title="Integration logs"
                  cancelButton={null}
                  confirmButton={null}
                  trigger={
                    <Button variant="outline" size="sm">
                      Logs
                    </Button>
                  }
                  size="5xl"
                >
                  <EventsLog integrationId={apiKey.integrationId} hideResend={true} />
                </Dialog>
              )}

              <Button variant="outline" size="sm" onClick={() => handleDeleteClick(apiKey)}>
                Delete
              </Button>
            </div>
          );
        },
      },
    ],
    [],
  );

  if (isError) {
    return (
      <Alert variant="destructive" className="my-4">
        <ExclamationCircleIcon />
        <AlertDescription>Failed to load API keys</AlertDescription>
      </Alert>
    );
  }

  const apiKeys = data?.apiKeys ?? [];

  const emptyState = (
    <EmptyState>
      <EmptyState.Title>No API keys yet</EmptyState.Title>
      <EmptyState.Subtitle>Create your first API key to get started.</EmptyState.Subtitle>
    </EmptyState>
  );

  return (
    <>
      <DataGrid columns={columns} data={apiKeys} isLoading={isLoading} emptyState={emptyState} />

      <ApiKeyDeleteDialog
        open={deleteDialogOpen}
        onClose={() => {
          setDeleteDialogOpen(false);
          setSelectedApiKey(null);
        }}
        apiKey={selectedApiKey}
      />
    </>
  );
};

export default ApiKeyTable;
